import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { PxTooltipDirective } from '../../../centrui/src/app/directives/px-tooltip/px-tooltip.directive';
import { combineLatest, filter, map, startWith, Subscription } from 'rxjs';
import { PlatformService } from '../../../services/platform.service';
import { CentruiHelpFormComponent } from '../../../centrui/src/app/components/centrui-help-form/centrui-help-form.component';
import { CentruiIconComponent } from '../../../centrui/src/app/components/centrui-icon/centrui-icon.component';
import { CentruiSpinnerComponent } from '../../../centrui/src/app/components/centrui-spinner/centrui-spinner.component';
import { AuthService } from '../../../centrui/src/app/services/auth.service';
import { ExternalLinkService } from '../../../centrui/src/app/services/external-link.service';
import { LumiMode } from '../../constants/LumiMode';
import { LumiSkill } from '../../constants/LumiSkill';
import { Project } from '../../models/Project';
import { AuxService } from '../../services/aux.service';
import { LumiConversation, LumiService } from '../../services/lumi.service';
import { LumiLoadingComponent } from '../lumi-loading/lumi-loading.component';
import { LumiChatComponent } from './lumi-chat/lumi-chat.component';
import { Feature } from '../../constants/Feature';

export interface FreshDeskTicket {
  id: number;
  status: string;
  updated_at: string;
}

export type Conversation = LumiConversation;

@Component({
  selector: 'app-lumi',
  imports: [
    CentruiIconComponent,
    CentruiSpinnerComponent,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    CentruiHelpFormComponent,
    LumiChatComponent,
    PxTooltipDirective,
    LumiLoadingComponent,
  ],
  templateUrl: './lumi.component.html',
  styleUrl: './lumi.component.scss',
  animations: [
    trigger('panelEnter', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(0.5rem)' }),
        animate('200ms 150ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
    trigger('fadeInUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(0.5rem)' }),
        animate('200ms {{ delay }}ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ], { params: { delay: 0 } }),
    ]),
  ],
})
export class LumiComponent implements OnInit, OnDestroy, AfterViewInit {
  addin = false;
  freshDeskHelpForm!: FormGroup;
  loading = true;
  mode: LumiMode = LumiMode.HOME;
  LumiMode = LumiMode;
  LumiSkill = LumiSkill;
  Feature = Feature;
  helpFormTicketNumber?: string;
  titleAction?: string;
  project?: Project;
  isOnProjectPage = false;
  conversations: Conversation[] = [];
  freshDeskTickets: FreshDeskTicket[] = [];
  loadingFreshDeskTickets = false;
  private static readonly EXPANDED_KEY = 'lumi-expanded';
  private static readonly EXPANDABLE_MODES: LumiMode[] = [LumiMode.POLARIX_HELP, LumiMode.PROJECT_HELP];

  loadingConversations = false;
  loadingMoreConversations = false;
  hasMoreConversations = false;
  private conversationsPage = 1;
  private conversationsPageSize = 10;
  private conversationStaggerOffset = 0;
  expanded = false;
  activeThreadId?: string;
  activeSkill?: LumiSkill;

  private projectSubscription!: Subscription;
  private routerSubscription!: Subscription;
  private contentResizeObserver?: ResizeObserver;

  get userFirstName(): string {
    const name = this.authService.userObject?.name;
    return name?.split(' ')[0] ?? '';
  }

  @ViewChild('lumiPopover') private lumiPopoverRef?: ElementRef<HTMLElement>;
  @ViewChild('lumiContent') private lumiContentRef?: ElementRef<HTMLElement>;
  @ViewChild('historyList') private historyListRef?: ElementRef<HTMLElement>;
  @ViewChild(LumiChatComponent) private lumiChat?: LumiChatComponent;

  lumiPanelOpen = false;

  constructor(
    private formBuilder: FormBuilder,
    public authService: AuthService,
    private lumiService: LumiService,
    private auxService: AuxService,
    private externalLinkService: ExternalLinkService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    protected platformService: PlatformService,
    private router: Router,
  ) {
    this.freshDeskHelpForm = this.formBuilder.group({
      description: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.expanded = localStorage.getItem(LumiComponent.EXPANDED_KEY) === 'true';

    this.projectSubscription = this.lumiService.project$.subscribe(project => {
      this.project = project;
      this.cdr.markForCheck();
    });

    this.routerSubscription = combineLatest([
      this.platformService.projectSlug$,
      this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map(() => this.router.url),
        startWith(this.router.url),
      ),
    ]).subscribe(([slug, url]) => {
      const wasOnProjectPage = this.isOnProjectPage;
      this.isOnProjectPage = !!slug || url.includes('/projects/scp-import-preview');
      if (wasOnProjectPage && !this.isOnProjectPage && this.mode === LumiMode.PROJECT_HELP) {
        this.setMode(LumiMode.HOME);
      }
      this.cdr.markForCheck();
    });
  }

  ngAfterViewInit(): void {
    this.setupContentResizeObserver();
  }

  private setupContentResizeObserver(): void {
    if (!this.lumiContentRef?.nativeElement) return;

    this.contentResizeObserver = new ResizeObserver(() => {
      this.ngZone.run(() => this.updatePanelHeight());
    });

    this.contentResizeObserver.observe(this.lumiContentRef.nativeElement);
  }

  private updatePanelHeight(): void {
    if (!this.lumiContentRef?.nativeElement) return;

    const content = this.lumiContentRef.nativeElement;
    const panel = content.querySelector('.lumi-mode-panel') as HTMLElement;

    if (!panel) return;

    const panelHeight = panel.scrollHeight;
    const style = getComputedStyle(content);
    const paddingTop = parseFloat(style.paddingTop) || 0;
    const paddingBottom = parseFloat(style.paddingBottom) || 0;
    const maxHeight = parseFloat(style.maxHeight) || Infinity;
    const totalHeight = Math.min(panelHeight + paddingTop + paddingBottom, maxHeight);

    content.style.height = `${totalHeight}px`;
  }

  onLumiLauncherClick(): void {
    this.lumiPanelOpen = !this.lumiPanelOpen;
    if (this.lumiPanelOpen) {
      this.focusChatInputIfInChatMode();
      setTimeout(() => this.updatePanelHeight(), 0);
    }
  }

  closeLumiPanel(): void {
    this.lumiPanelOpen = false;
  }

  toggleExpanded(): void {
    this.expanded = !this.expanded;
    localStorage.setItem(LumiComponent.EXPANDED_KEY, String(this.expanded));
    if (this.expanded) {
      this.focusChatInputIfInChatMode();
    }
  }

  ngOnDestroy(): void {
    this.projectSubscription.unsubscribe();
    this.routerSubscription.unsubscribe();
    this.contentResizeObserver?.disconnect();
  }

  private focusChatInputIfInChatMode(): void {
    if (this.mode !== LumiMode.POLARIX_HELP && this.mode !== LumiMode.PROJECT_HELP) return;
    this.lumiChat?.focusMessageInput();
  }

  get isExpandable(): boolean {
    return LumiComponent.EXPANDABLE_MODES.includes(this.mode);
  }

  setMode(mode: LumiMode, thread_id?: string, skill?: LumiSkill) {
    this.mode = mode;
    this.titleAction = undefined;
    this.helpFormTicketNumber = undefined;
    this.activeThreadId = thread_id;

    if (mode === LumiMode.POLARIX_HELP || mode === LumiMode.PROJECT_HELP) {
      this.activeSkill = skill ?? (mode === LumiMode.PROJECT_HELP ? LumiSkill.PROJECT_QA : LumiSkill.PLATFORM_QA);
    } else {
      this.activeSkill = undefined;
    }

    if (LumiComponent.EXPANDABLE_MODES.includes(mode)) {
      this.expanded = localStorage.getItem(LumiComponent.EXPANDED_KEY) === 'true';
    } else {
      this.expanded = false;
    }

    switch (mode) {
      case LumiMode.HELP_REQUEST:
        this.titleAction = 'Submit help request';
        break;
      case LumiMode.POLARIX_HELP:
        this.titleAction = 'Polarix help';
        break;
      case LumiMode.PROJECT_HELP:
        this.titleAction = this.project?.title + ' assistance';
        break;
      case LumiMode.HISTORY:
        this.titleAction = 'History';
        this.resetConversationsPagination();
        this.loadConversationsPage(1, false);
        break;
      case LumiMode.INFO:
        this.titleAction = 'Meet Lumi';
        break;
      case LumiMode.SUPPORT_MESSAGES:
        this.titleAction = 'Support messages';
        this.resetFreshDeskTickets();
        this.getFreshdeskTickets();
        break;
      default:
        this.titleAction = undefined;
        break;
    }

    setTimeout(() => this.updatePanelHeight(), 0);
  }

  dismissedHelpForm(result?: string) {
    if (result) {
      this.helpFormTicketNumber = result;
      this.titleAction = 'Back';
    } else {
      this.setMode(LumiMode.HOME);
    }
  }

  helpRequestBack(): void {
    if (this.helpFormTicketNumber) {
      this.setMode(LumiMode.SUPPORT_MESSAGES);
    } else {
      this.setMode(LumiMode.HOME);
    }
  }

  openFreshDeskTicket(ticketId: number) {
    this.externalLinkService.warn(`https://ninelabs.freshdesk.com/a/tickets/${ticketId}`);
  }

  private resetFreshDeskTickets(): void {
    this.freshDeskTickets = [];
    this.loadingFreshDeskTickets = false;
  }

  private normalizeFreshDeskTickets(response: unknown): FreshDeskTicket[] {
    if (Array.isArray(response)) {
      return response as FreshDeskTicket[];
    }
    if (response && typeof response === 'object') {
      const payload = (response as { data?: unknown }).data ?? response;
      if (Array.isArray(payload)) {
        return payload as FreshDeskTicket[];
      }
    }
    return [];
  }

  getFreshdeskTickets() {
    this.loadingFreshDeskTickets = true;
    this.auxService.getFreshdeskTickets().subscribe({
      next: (response: unknown) => {
        this.freshDeskTickets = this.normalizeFreshDeskTickets(response);
        this.loadingFreshDeskTickets = false;
        this.cdr.markForCheck();
        setTimeout(() => this.updatePanelHeight(), 0);
      },
      error: (error: unknown) => {
        this.loadingFreshDeskTickets = false;
        this.cdr.markForCheck();
        console.error(error);
      },
    });
  }

  onHistoryScroll(): void {
    if (this.loadingConversations || this.loadingMoreConversations || !this.hasMoreConversations) return;

    const el = this.historyListRef?.nativeElement;
    if (!el) return;

    if (el.scrollHeight <= el.clientHeight) {
      return;
    }

    const threshold = 80;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
      this.loadMoreConversations();
    }
  }

  private resetConversationsPagination(): void {
    this.conversations = [];
    this.conversationsPage = 1;
    this.hasMoreConversations = false;
    this.loadingConversations = false;
    this.loadingMoreConversations = false;
    this.conversationStaggerOffset = 0;
  }

  conversationFadeInDelay(index: number): number {
    const local = index - this.conversationStaggerOffset;
    return local >= 0 ? (local + 1) * 100 : 0;
  }

  trackByConversation(_index: number, c: Conversation): string {
    return c.thread_id;
  }

  isProjectConversation(c: Conversation): boolean {
    if (c.project != null) return true;
    if (c.project_id != null) return true;
    if (c.project_slug != null && String(c.project_slug).trim() !== '') return true;
    const intent = (c.intent ?? '').toLowerCase().replace(/-/g, '_');
    return intent === 'project_help' || intent === 'project_qa' || intent.startsWith('project_');
  }

  openConversationFromHistory(c: Conversation): void {
    if (this.isProjectConversation(c)) {
      if (c.project?.id && c.project.id !== this.project?.id) {
        this.lumiService.setProject(c.project);
      }
      this.setMode(LumiMode.PROJECT_HELP, c.thread_id, LumiSkill.PROJECT_QA);
    } else {
      this.setMode(LumiMode.POLARIX_HELP, c.thread_id, LumiSkill.PLATFORM_QA);
    }
  }

  private loadConversationsPage(page: number, append: boolean): void {
    if (append) {
      this.loadingMoreConversations = true;
    } else {
      this.loadingConversations = true;
    }

    this.lumiService.getConversations(page, this.conversationsPageSize).subscribe({
      next: (response) => {
        const payload = (response as { data?: typeof response }).data ?? response;
        const pageNum = Number(payload.page ?? page);
        const pageSize = Number(payload.page_size ?? this.conversationsPageSize);
        const total = Number(payload.total ?? 0);
        const rawConversations = Array.isArray(payload.conversations) ? payload.conversations : [];
        this.conversationsPage = pageNum;
        this.conversationsPageSize = pageSize;
        this.hasMoreConversations = pageNum * pageSize < total;
        const conversations = rawConversations.map((c) => ({
          ...c,
          title: this.stripUserInputTags(c.title),
        }));
        if (append) {
          this.conversationStaggerOffset = this.conversations.length;
          this.conversations = [...this.conversations, ...conversations];
        } else {
          this.conversationStaggerOffset = 0;
          this.conversations = conversations;
        }
        this.loadingConversations = false;
        this.loadingMoreConversations = false;
        this.cdr.markForCheck();
        setTimeout(() => this.updatePanelHeight(), 0);
      },
      error: () => {
        this.loadingConversations = false;
        this.loadingMoreConversations = false;
        this.cdr.markForCheck();
      },
    });
  }

  private stripUserInputTags(title?: string | null): string {
    if (!title) return '';
    return title.replace(/<\/?USER_INPUT>/gi, '').trim();
  }

  private loadMoreConversations(): void {
    if (!this.hasMoreConversations || this.loadingMoreConversations || this.loadingConversations) return;
    this.loadConversationsPage(this.conversationsPage + 1, true);
  }

  getTicketStatusClass(status: string | number): string {
    const n = typeof status === 'string' ? Number(status) : status;
    switch (n) {
      case 3:
        return 'pending';
      case 4:
        return 'success';
      case 5:
        return 'disabled';
    }
    return '';
  }
}
